import { CharterFareInventory, CharterInventoryFlight } from '~/components/Flights/Charter/charter.types';

import * as TimeService from '~/services/TimeService';

import currencyFormatter, { addDollarType } from '~/utils/currencyFormatter';

export const cabinLabel: Record<string, string> = {
  BUSINESS: 'Business',
  ECONOMY: 'Economy',
  PREMIUM_ECONOMY: 'Premium Economy',
};

export const formatAirportInput = (value: string) => {
  return value.trim().toUpperCase().slice(0, 3);
};

export const compareByDate = (left: string, right: string) => {
  const leftDate = TimeService.getDayJs(left);
  const rightDate = TimeService.getDayJs(right);

  if (leftDate.isValid() && rightDate.isValid()) {
    return leftDate.valueOf() - rightDate.valueOf();
  }

  return left.localeCompare(right);
};

export const formatDateTime = (date: string, time: string) => {
  if (!date) {
    return '—';
  }
  const dateTime = TimeService.getDayJs(`${date}T${time || '00:00'}`);
  if (!dateTime.isValid()) {
    return date;
  }
  return dateTime.format('DD MMM YYYY, HH:mm');
};

export const formatDate = (date: string) => {
  if (!date) {
    return '—';
  }
  const dateObj = TimeService.getDayJs(date);
  if (!dateObj.isValid()) {
    return date;
  }
  return dateObj.format('DD MMM YYYY');
};

export const formatTime = (time: string) => {
  if (!time) {
    return '—';
  }
  // Assuming time is HH:mm or HH:mm:ss. formatting it to HH:mm
  // We can parse it by attaching a dummy date
  const timeObj = TimeService.getDayJs(`2000-01-01T${time}`);
  if (!timeObj.isValid()) {
    return time;
  }
  return timeObj.format('HH:mm');
};

export const formatFlightNumber = (flightNumber: string, airlineCode?: string) => {
  if (!flightNumber) {
    return '—';
  }
  return airlineCode ? `${airlineCode} ${flightNumber}` : flightNumber;
};

export const formatPrice = (
  fare: CharterFareInventory,
  priceKey: keyof Pick<
    CharterFareInventory,
    'priceAdult' | 'priceChild' | 'priceInfant' | 'taxAdult' | 'taxChild' | 'taxInfant'
  >,
): string => {
  const value = fare[priceKey];
  const formatted = currencyFormatter(fare.currency, value, 2);
  return addDollarType(formatted, fare.currency);
};

export const availableSeats = (fare: CharterFareInventory) => {
  return Math.max(fare.allocation - fare.booked, 0);
};

const cabinClassToOverrideCabin: Record<CharterFareInventory['cabinClass'], string> = {
  ECONOMY: 'economy',
  PREMIUM_ECONOMY: 'premium_economy',
  BUSINESS: 'business',
};

export const blockedSeats = (flight: CharterInventoryFlight, fare: CharterFareInventory): number => {
  if (!flight.seatConfigurationOverrides) {
    return 0;
  }
  const overrideCabin = cabinClassToOverrideCabin[fare.cabinClass];
  const cabinOverride = flight.seatConfigurationOverrides.find((override) => override.cabin === overrideCabin);
  if (!cabinOverride) {
    return 0;
  }
  return cabinOverride.overrides.filter((override) => !override.isAvailable).length;
};

export const blockedSeatsByReason = (
  flight: CharterInventoryFlight,
  fare: CharterFareInventory,
  reason: 'VVIP' | 'CREW_REST' | 'DUMMY',
): number => {
  if (!flight.seatConfigurationOverrides) {
    return 0;
  }
  const overrideCabin = cabinClassToOverrideCabin[fare.cabinClass];
  const cabinOverride = flight.seatConfigurationOverrides.find((override) => override.cabin === overrideCabin);
  if (!cabinOverride) {
    return 0;
  }

  return cabinOverride.overrides.filter((override) => {
    if (override.isAvailable) {
      return false;
    }
    const blockReason = override.blockReason?.toUpperCase();
    return blockReason === reason;
  }).length;
};
